// Who can do what.
//
// ---------------------------------------------------------------------------
// Why this is a capability model rather than `if (role === 'admin')`
// ---------------------------------------------------------------------------
//
// Role checks scattered through route handlers rot in a specific way: a new
// endpoint gets `role !== 'preparer'` because that was what the neighbouring
// handler did, and six months later nobody can answer "what exactly can a
// Manager do?" without reading forty files.
//
// Here there is one table. Every route asks `can(actor, 'templates.publish')`,
// and the answer to "what can a Manager do?" is a list you can read.
//
// This module is PURE — no database, no request, no I/O. That is what makes it
// cheap to test exhaustively, which matters because a permission bug is not a
// broken button: it is one client's tax return visible to the wrong person.
//
// ---------------------------------------------------------------------------
// The four tiers
// ---------------------------------------------------------------------------
//
//   client    The taxpayer. Their own data, nothing else. Never a `StaffActor`
//             — client authorisation is handled separately and by construction
//             (every /api/me route is scoped to the session's own client id).
//
//   preparer  An accountant working returns. Sees EVERY client, but may only
//             CHANGE the ones assigned to them. See the note on visibility
//             below — this was a deliberate decision with a real trade-off.
//
//   manager   Configures templates and questionnaires, reviews reports, and
//             assigns work to preparers. No staff administration.
//
//   admin     Everything above, plus staff, settings and the audit log.
//
//   SuperUser An admin with two extra powers: only they may promote or demote
//             administrators, and they cannot be demoted or disabled by
//             anyone. Exactly one exists.
// ---------------------------------------------------------------------------
// Roles
// ---------------------------------------------------------------------------
export const STAFF_ROLES = ['preparer', 'manager', 'admin'];
/**
 * Addresses permitted to hold `admin`, while the system is being built.
 *
 * A hardcoded allowlist is a blunt instrument, and that is the point: it means
 * a compromised admin account cannot quietly promote an attacker's address,
 * because the check does not consult the database. Revisit when the product is
 * sold to other practices — at which point this becomes per-tenant
 * configuration rather than a constant.
 */
export const ADMIN_ALLOWLIST = [
    'zdeklerk@xeta.com.au',
    'dwright@xeta.com.au',
    'dedewet@xeta.com.au',
];
/** The single SuperUser. Final say over who administers the system. */
export const SUPER_USER_EMAIL = 'zdeklerk@xeta.com.au';
export function normaliseEmail(email) {
    return email.trim().toLowerCase();
}
export function isAdminAllowed(email) {
    return ADMIN_ALLOWLIST.includes(normaliseEmail(email));
}
export function isSuperUserEmail(email) {
    return normaliseEmail(email) === SUPER_USER_EMAIL;
}
// ---------------------------------------------------------------------------
// Capabilities
// ---------------------------------------------------------------------------
/**
 * Every distinct thing a staff member can attempt.
 *
 * Named `noun.verb` so they sort usefully, and deliberately granular: the
 * difference between `clients.view` and `clients.edit`, or between
 * `templates.edit` and `templates.publish`, is where the interesting decisions
 * live. Collapsing them would mean a Manager who can draft a template can also
 * push it to every client.
 */
export const CAPABILITIES = [
    // --- Clients -------------------------------------------------------------
    'clients.view', // see the client list and client details
    'clients.edit', // change name, code, email, entity type
    'clients.invite', // send or re-send a client invitation
    'clients.archive',
    'clients.signInAs', // open a client's questionnaire AS them — impersonation
    // --- Questionnaires ------------------------------------------------------
    'questionnaire.view', // read a client's decrypted answers
    'questionnaire.edit', // change answers or status on a client's behalf
    'questionnaire.export', // produce the Excel export
    // --- Templates and mapping ----------------------------------------------
    'templates.view',
    'templates.edit', // edit a DRAFT year
    'templates.publish', // make a year visible to clients
    'templates.rollForward',
    'mappings.edit', // where questions land on the return
    // --- Workload ------------------------------------------------------------
    'assignments.view',
    'assignments.manage', // assign clients to preparers
    // --- Reporting -----------------------------------------------------------
    'reports.view',
    // --- Administration ------------------------------------------------------
    'staff.view',
    'staff.manage', // create, edit and disable preparers and managers
    'staff.manageAdmins', // promote or demote administrators — SuperUser only
    'audit.view',
    'settings.manage', // practice details, integrations, email
];
// ---------------------------------------------------------------------------
// Role → capabilities
// ---------------------------------------------------------------------------
/**
 * What a PREPARER can do.
 *
 * Note `clients.view` and `questionnaire.view` are present while
 * `questionnaire.edit` is not: a preparer can read every client but may only
 * change the ones assigned to them. That conditional part is not expressible
 * in a flat list, so it lives in `canForClient()` below.
 *
 * On visibility: letting every preparer read every client means every
 * preparer can see every TFN and every income figure. That was chosen
 * deliberately over assignment-scoped visibility, because staff cover for each
 * other and a preparer who cannot open a client they have been asked about is
 * a daily obstruction. The mitigation is that every read is audited, so the
 * question "who looked at this return?" always has an answer.
 */
const PREPARER_CAPABILITIES = [
    'clients.view',
    'questionnaire.view',
    'questionnaire.export',
    'templates.view',
    'assignments.view',
    'reports.view',
];
/** A manager does everything a preparer does, plus configuration and workload. */
const MANAGER_CAPABILITIES = [
    ...PREPARER_CAPABILITIES,
    'clients.edit',
    'clients.invite',
    'clients.signInAs',
    // A manager CAN edit any client's questionnaire — that is the escalation
    // path when a preparer is away and a client needs something changed.
    'questionnaire.edit',
    'templates.edit',
    'templates.publish',
    'templates.rollForward',
    'mappings.edit',
    'assignments.manage',
    'staff.view',
];
/**
 * An admin does everything a manager does, plus staff and settings.
 *
 * Deliberately WITHOUT `staff.manageAdmins`. An admin can create and manage
 * preparers and managers, but cannot make another admin or remove one — that
 * is the SuperUser's alone, which is what stops the admin tier from being
 * self-expanding.
 */
const ADMIN_CAPABILITIES = [
    ...MANAGER_CAPABILITIES,
    'clients.archive',
    'staff.manage',
    'audit.view',
    'settings.manage',
];
const ROLE_CAPABILITIES = {
    preparer: PREPARER_CAPABILITIES,
    manager: MANAGER_CAPABILITIES,
    admin: ADMIN_CAPABILITIES,
};
/** Everything this actor can do, for the UI to hide what it cannot. */
export function capabilitiesOf(actor) {
    const base = [...ROLE_CAPABILITIES[actor.role]];
    // The SuperUser's single addition.
    if (actor.isSuperUser && !base.includes('staff.manageAdmins')) {
        base.push('staff.manageAdmins');
    }
    return base;
}
/**
 * Can this actor do this thing?
 *
 * The one function every route guard calls. Hiding a button in the UI is a
 * courtesy; this is the actual boundary.
 */
export function can(actor, capability) {
    if (capability === 'staff.manageAdmins') {
        // Not role-derived at all. Only the SuperUser, regardless of role.
        return actor.isSuperUser;
    }
    if (actor.isSuperUser) {
        // A SuperUser is an admin plus the above, so they hold every capability.
        return true;
    }
    return ROLE_CAPABILITIES[actor.role].includes(capability);
}
/** Throwing variant, for route handlers. */
export class PermissionDenied extends Error {
    capability;
    constructor(capability) {
        super('You do not have permission to do that.');
        this.capability = capability;
        this.name = 'PermissionDenied';
    }
}
export function require_(actor, capability) {
    if (!can(actor, capability))
        throw new PermissionDenied(capability);
}
// ---------------------------------------------------------------------------
// Client-scoped permission
// ---------------------------------------------------------------------------
/**
 * Can this actor do this thing TO THIS CLIENT?
 *
 * Separate from `can()` because a preparer's write access depends on
 * assignment, which `can()` has no way to know. Everything that modifies a
 * specific client's data must go through here rather than through `can()`
 * alone — that is the whole difference between "can edit questionnaires" and
 * "can edit THIS questionnaire".
 *
 * `assignedStaffIds` is the set of staff assigned to the client in question.
 */
export function canForClient(actor, capability, assignedStaffIds) {
    if (!can(actor, capability))
        return false;
    // Managers, admins and the SuperUser are not assignment-scoped: they are the
    // escalation path when the assigned preparer is unavailable.
    if (actor.role !== 'preparer')
        return true;
    // A preparer reading is unrestricted; a preparer writing is not.
    const WRITE_CAPABILITIES = [
        'clients.edit',
        'clients.invite',
        'clients.archive',
        'clients.signInAs',
        'questionnaire.edit',
    ];
    if (!WRITE_CAPABILITIES.includes(capability))
        return true;
    return assignedStaffIds.includes(actor.staffId);
}
/**
 * May `actor` change `target`'s role to `newRole`?
 *
 * Collected here rather than inline in the route because the rules interact,
 * and because every one of them exists to prevent a specific way the admin
 * tier could be subverted.
 */
export function canChangeRole(actor, target, newRole) {
    // 1. Nobody touches the SuperUser. Not another admin, not themselves by
    //    accident. This is what makes "only I may instruct final changes" real
    //    rather than a convention.
    if (target.isSuperUser) {
        return {
            ok: false,
            reason: 'cannot-change-super-user',
            message: 'The SuperUser account cannot be changed.',
        };
    }
    // 2. Granting or revoking admin is the SuperUser's alone. Without this, any
    //    admin could appoint another admin and the tier would be
    //    self-expanding — one compromised account becomes permanent access.
    const involvesAdmin = newRole === 'admin' || target.role === 'admin';
    if (involvesAdmin && !can(actor, 'staff.manageAdmins')) {
        return {
            ok: false,
            reason: 'not-permitted',
            message: 'Only the SuperUser can add or remove administrators.',
        };
    }
    if (!involvesAdmin && !can(actor, 'staff.manage')) {
        return {
            ok: false,
            reason: 'not-permitted',
            message: 'You do not have permission to change roles.',
        };
    }
    // 3. The allowlist, checked at the point of GRANT rather than only at
    //    creation. Otherwise an account created as a preparer could be promoted
    //    to admin later and bypass it entirely.
    if (newRole === 'admin' && !isAdminAllowed(target.email)) {
        return {
            ok: false,
            reason: 'admin-not-allowlisted',
            message: `${target.email} is not on the administrator allowlist.`,
        };
    }
    // 4. No self-demotion. An admin removing their own access is almost always a
    //    mistake, and it can leave the system with nobody able to put it right.
    if (actor.staffId === target.staffId && newRole !== actor.role) {
        return {
            ok: false,
            reason: 'cannot-change-self',
            message: 'You cannot change your own role. Ask the SuperUser.',
        };
    }
    return { ok: true };
}
/**
 * May `actor` disable `target`'s account?
 *
 * Same shape as role changes and for the same reason: the SuperUser must stay
 * reachable, and locking yourself out is never what you meant.
 */
export function canDisableStaff(actor, target) {
    if (target.isSuperUser) {
        return {
            ok: false,
            reason: 'super-user-cannot-be-demoted',
            message: 'The SuperUser account cannot be disabled.',
        };
    }
    if (actor.staffId === target.staffId) {
        return {
            ok: false,
            reason: 'cannot-change-self',
            message: 'You cannot disable your own account.',
        };
    }
    if (target.role === 'admin' && !can(actor, 'staff.manageAdmins')) {
        return {
            ok: false,
            reason: 'not-permitted',
            message: 'Only the SuperUser can disable an administrator.',
        };
    }
    if (!can(actor, 'staff.manage')) {
        return {
            ok: false,
            reason: 'not-permitted',
            message: 'You do not have permission to disable accounts.',
        };
    }
    return { ok: true };
}
// ---------------------------------------------------------------------------
// Presentation
// ---------------------------------------------------------------------------
/** Human label for a role, for the UI and for emails. */
export function roleLabel(role, isSuperUser = false) {
    if (isSuperUser)
        return 'SuperUser';
    return { preparer: 'Preparer', manager: 'Manager', admin: 'Administrator' }[role];
}
/** One line explaining what a role can do, for the staff admin screen. */
export function roleDescription(role) {
    return {
        preparer: 'Works tax returns. Sees every client, and can change the ones assigned to them.',
        manager: 'Configures templates and questionnaires, assigns work to preparers, and reviews reports.',
        admin: 'Full access, including staff, settings and the audit log.',
    }[role];
}
//# sourceMappingURL=permissions.js.map